import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useCreateVendor, useUpdateVendor, type CreateVendorInput, type VendorType, type PaymentTerms, type Vendor } from "@/hooks/useVendors";
import { useCreateVendorInsurance, type InsuranceType, type CreateInsuranceInput } from "@/hooks/useVendorInsurance";
import { useCreateVendorLicense, type CreateLicenseInput } from "@/hooks/useVendorLicenses";
import { toast } from "sonner";
import { Plus, Shield, FileText, Receipt, ScrollText, CheckCircle2, XCircle, AlertTriangle, Trash2, User, Scale } from "lucide-react";

const VENDOR_TYPES: { value: VendorType; label: string; icon: string }[] = [
  { value: "subcontractor", label: "Subcontractor", icon: "👷" },
  { value: "material_supplier", label: "Material Supplier", icon: "🧱" },
  { value: "equipment_rental", label: "Equipment Rental", icon: "🏗" },
  { value: "general", label: "General", icon: "📋" },
];

const PAYMENT_TERMS: { value: string; label: string }[] = [
  { value: "due_on_receipt", label: "Due on Receipt" },
  { value: "net_15", label: "Net 15" },
  { value: "net_30", label: "Net 30" },
  { value: "net_45", label: "Net 45" },
  { value: "net_60", label: "Net 60" },
  { value: "custom", label: "Custom" },
];

const LICENSE_TYPES = [
  "General Contractor", "Flooring Specialty", "Roofing", "Electrical",
  "Plumbing", "HVAC", "Other",
];

const INSURANCE_TYPE_LABELS: Record<InsuranceType, string> = {
  general_liability: "General Liability",
  workers_comp: "Workers Compensation",
  umbrella: "Umbrella / Excess",
  commercial_auto: "Commercial Auto",
  professional_liability: "Professional Liability",
  builders_risk: "Builders Risk",
  other: "Other",
};

const LIEN_WAIVER_OPTIONS = [
  { value: "both", label: "Both (conditional progress + unconditional final)" },
  { value: "final_only", label: "Final only" },
  { value: "custom", label: "Custom" },
];

const BUSINESS_TYPES = [
  { value: "llc", label: "LLC" },
  { value: "corporation", label: "Corporation" },
  { value: "s_corp", label: "S-Corp" },
  { value: "sole_proprietor", label: "Sole Proprietor" },
  { value: "partnership", label: "Partnership" },
  { value: "individual", label: "Individual" },
  { value: "other", label: "Other" },
];

const WC_OPTIONS = [
  { value: "has_coverage", label: "I have Workers Comp coverage" },
  { value: "non_subscriber", label: "Texas Non-Subscriber" },
  { value: "sole_proprietor_exempt", label: "Sole Proprietor Exempt" },
];

interface LicenseEntry {
  license_type: string;
  license_number: string;
  issuing_state: string;
  expiry_date: string;
}

interface InsuranceEntry {
  insurance_type: InsuranceType;
  carrier_name: string;
  policy_number: string;
  agent_name: string;
  agent_phone: string;
  per_occurrence_limit: string;
  aggregate_limit: string;
  effective_date: string;
  expiry_date: string;
  company_additional_insured: boolean;
  wc_option: string;
}

const emptyLicense = (): LicenseEntry => ({
  license_type: "General Contractor", license_number: "", issuing_state: "", expiry_date: "",
});

const emptyInsurance = (type: InsuranceType): InsuranceEntry => ({
  insurance_type: type, carrier_name: "", policy_number: "", agent_name: "", agent_phone: "",
  per_occurrence_limit: "", aggregate_limit: "", effective_date: "", expiry_date: "",
  company_additional_insured: false, wc_option: "has_coverage",
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  onCreated?: (vendor: { id: string; name: string }) => void;
  editVendor?: Vendor | null;
  forceTrack?: "subcontractor" | "personnel";
}

export const NewVendorDialog = ({ open, onOpenChange, initialName, onCreated, editVendor, forceTrack }: Props) => {
  const createVendor = useCreateVendor();
  const updateVendor = useUpdateVendor();
  const createInsurance = useCreateVendorInsurance();
  const createLicense = useCreateVendorLicense();
  const isEdit = !!editVendor;

  const [step, setStep] = useState(1);
  // Step 1
  const [name, setName] = useState("");
  const [vendorTypes, setVendorTypes] = useState<VendorType[]>(["subcontractor"]);
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("net_30");
  const [customDays, setCustomDays] = useState("");
  const [notes, setNotes] = useState("");

  // Step 2 — Licenses (subcontractor)
  const [licenses, setLicenses] = useState<LicenseEntry[]>([emptyLicense()]);
  // Step 2 — Insurance (subcontractor)
  const [insurances, setInsurances] = useState<InsuranceEntry[]>([
    emptyInsurance("general_liability"),
    emptyInsurance("workers_comp"),
  ]);
  // Step 2 — Tax & W9 (both tracks)
  const [taxIdType, setTaxIdType] = useState("ein");
  const [taxId, setTaxId] = useState("");
  const [businessType, setBusinessType] = useState("llc");
  const [w9OnFile, setW9OnFile] = useState(false);
  const [w9ReceivedDate, setW9ReceivedDate] = useState("");
  const [is1099Eligible, setIs1099Eligible] = useState(false);
  const [ein, setEin] = useState("");
  // Step 2 — Contract Terms (subcontractor)
  const [defaultRetainagePct, setDefaultRetainagePct] = useState("10");
  const [lienWaiverRequired, setLienWaiverRequired] = useState(true);
  const [lienWaiverType, setLienWaiverType] = useState("both");
  const [onboardingNotes, setOnboardingNotes] = useState("");
  // Supplier fields
  const [accountNumber, setAccountNumber] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  // Personnel-specific
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");
  // IC Agreement
  const [icAgreed, setIcAgreed] = useState(false);
  const [icSignatureName, setIcSignatureName] = useState("");

  // Determine track
  const isSub = forceTrack === "subcontractor" || (!forceTrack && vendorTypes.includes("subcontractor"));
  const isPersonnel = forceTrack === "personnel";
  const isSupplier = !isSub && !isPersonnel;
  const track = isPersonnel ? "personnel" : isSub ? "subcontractor" : "subcontractor";

  useEffect(() => {
    if (open) {
      setStep(1);
      setIcAgreed(false);
      setIcSignatureName("");
      if (editVendor) {
        setName(editVendor.name);
        setVendorTypes((editVendor.vendor_types as VendorType[]) || [editVendor.vendor_type as VendorType || "subcontractor"]);
        setContactName(editVendor.contact_name || "");
        setEmail(editVendor.email || "");
        setPhone(editVendor.phone || "");
        setBillingAddress(editVendor.billing_address || "");
        setBillingCity(editVendor.billing_city || "");
        setBillingState(editVendor.billing_state || "");
        setBillingZip(editVendor.billing_zip || "");
        const pt = editVendor.payment_terms || "net_30";
        setPaymentTerms(pt);
        setCustomDays((editVendor as any).payment_terms_custom_days?.toString() || "");
        setNotes(editVendor.notes || "");
        setEin(editVendor.ein || "");
        setW9OnFile(editVendor.w9_on_file || false);
        setW9ReceivedDate((editVendor as any).w9_received_date || "");
        setIs1099Eligible(editVendor.is_1099_eligible || false);
        setLienWaiverRequired(editVendor.lien_waiver_required ?? true);
        setDefaultRetainagePct(String(editVendor.default_retainage_pct ?? 10));
        setOnboardingNotes((editVendor as any).onboarding_notes || "");
        setAccountNumber(editVendor.account_number || "");
        setCreditLimit(editVendor.credit_limit ? String(editVendor.credit_limit) : "");
        setLicenses([emptyLicense()]);
        setInsurances([emptyInsurance("general_liability"), emptyInsurance("workers_comp")]);
      } else {
        setName(initialName || "");
        setVendorTypes(forceTrack === "personnel" ? ["general"] : ["subcontractor"]);
        setContactName(""); setEmail(""); setPhone("");
        setBillingAddress(""); setBillingCity(""); setBillingState(""); setBillingZip("");
        setPaymentTerms("net_30"); setCustomDays(""); setNotes("");
        setLicenses([emptyLicense()]);
        setInsurances([emptyInsurance("general_liability"), emptyInsurance("workers_comp")]);
        setTaxIdType(forceTrack === "personnel" ? "ssn" : "ein");
        setTaxId(""); setBusinessType(forceTrack === "personnel" ? "individual" : "llc");
        setEin(""); setW9OnFile(false); setW9ReceivedDate(""); setIs1099Eligible(false);
        setLienWaiverRequired(true); setLienWaiverType("both"); setDefaultRetainagePct("10");
        setOnboardingNotes(""); setAccountNumber(""); setCreditLimit("");
        setEmergencyContactName(""); setEmergencyContactPhone("");
      }
    }
  }, [open, initialName, editVendor, forceTrack]);

  useEffect(() => {
    if (isSub && !isEdit) setIs1099Eligible(true);
  }, [isSub, isEdit]);

  const toggleType = (t: VendorType) => {
    setVendorTypes((prev) =>
      prev.includes(t) ? (prev.length > 1 ? prev.filter((v) => v !== t) : prev) : [...prev, t]
    );
  };

  // Compliance checklist (subcontractor only)
  const complianceChecks = useCallback(() => {
    if (!isSub) return [];
    const checks: { label: string; ok: boolean }[] = [];
    const hasLicense = licenses.some((l) => l.license_number.trim());
    checks.push({ label: "License on file", ok: hasLicense });
    const hasGL = insurances.some((i) => i.insurance_type === "general_liability" && i.carrier_name.trim());
    checks.push({ label: "General Liability COI", ok: hasGL });
    const hasWC = insurances.some((i) => i.insurance_type === "workers_comp" && (i.carrier_name.trim() || i.wc_option !== "has_coverage"));
    checks.push({ label: "Workers Comp COI / Exemption", ok: hasWC });
    checks.push({ label: "W9 on file", ok: w9OnFile });
    checks.push({ label: "Tax ID entered", ok: !!(ein.trim() || taxId.trim()) });
    return checks;
  }, [isSub, licenses, insurances, w9OnFile, ein, taxId]);

  // Personnel compliance checklist
  const personnelChecks = useCallback(() => {
    if (!isPersonnel) return [];
    return [
      { label: "Tax ID entered", ok: !!taxId.trim() },
      { label: "W9 on file", ok: w9OnFile },
      { label: "IC Agreement acknowledged", ok: icAgreed && !!icSignatureName.trim() },
    ];
  }, [isPersonnel, taxId, w9OnFile, icAgreed, icSignatureName]);

  const activeChecks = isSub ? complianceChecks() : isPersonnel ? personnelChecks() : [];
  const allComplianceOk = activeChecks.length === 0 || activeChecks.every((c) => c.ok);

  const updateLicense = (idx: number, field: keyof LicenseEntry, value: string) => {
    setLicenses((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const updateInsurance = (idx: number, field: keyof InsuranceEntry, value: any) => {
    setInsurances((prev) => prev.map((ins, i) => (i === idx ? { ...ins, [field]: value } : ins)));
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Company / vendor name is required");
    if (paymentTerms === "custom" && (!customDays || Number(customDays) < 1)) {
      return toast.error("Enter custom payment days (1-365)");
    }

    const complianceStatus = isPersonnel
      ? (allComplianceOk ? "approved" : "pending")
      : isSub
      ? (allComplianceOk ? "approved" : "incomplete")
      : "approved";

    const input: CreateVendorInput = {
      name: name.trim(),
      vendor_types: vendorTypes,
      contact_name: contactName.trim() || undefined,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      billing_address: billingAddress.trim() || undefined,
      billing_city: billingCity.trim() || undefined,
      billing_state: billingState.trim() || undefined,
      billing_zip: billingZip.trim() || undefined,
      payment_terms: paymentTerms as PaymentTerms,
      notes: notes.trim() || undefined,
      license_number: licenses[0]?.license_number?.trim() || undefined,
      license_state: licenses[0]?.issuing_state?.trim() || undefined,
      license_expiry: licenses[0]?.expiry_date || undefined,
      insurance_carrier: insurances[0]?.carrier_name?.trim() || undefined,
      insurance_expiry: insurances[0]?.expiry_date || undefined,
      w9_on_file: w9OnFile,
      ein: ein.trim() || taxId.trim() || undefined,
      is_1099_eligible: is1099Eligible,
      lien_waiver_required: lienWaiverRequired,
      default_retainage_pct: Number(defaultRetainagePct) || 10,
      account_number: accountNumber.trim() || undefined,
      credit_limit: creditLimit ? Number(creditLimit) : undefined,
    };

    try {
      if (isEdit) {
        await updateVendor.mutateAsync({ id: editVendor.id, ...input } as any);
        toast.success("Vendor updated");
      } else {
        const result = await createVendor.mutateAsync(input);
        // Create insurance records (subcontractor only)
        if (isSub) {
          for (const ins of insurances) {
            if (ins.carrier_name.trim() || (ins.insurance_type === "workers_comp" && ins.wc_option !== "has_coverage")) {
              await createInsurance.mutateAsync({
                vendor_id: result.id,
                insurance_type: ins.insurance_type,
                carrier_name: ins.carrier_name.trim() || undefined,
                policy_number: ins.policy_number.trim() || undefined,
                agent_name: ins.agent_name.trim() || undefined,
                agent_phone: ins.agent_phone.trim() || undefined,
                per_occurrence_limit: ins.per_occurrence_limit ? Number(ins.per_occurrence_limit) : undefined,
                aggregate_limit: ins.aggregate_limit ? Number(ins.aggregate_limit) : undefined,
                effective_date: ins.effective_date || undefined,
                expiry_date: ins.expiry_date || undefined,
                company_additional_insured: ins.company_additional_insured,
              });
            }
          }
          for (const lic of licenses) {
            if (lic.license_number.trim()) {
              await createLicense.mutateAsync({
                vendor_id: result.id,
                license_type: lic.license_type,
                license_number: lic.license_number.trim(),
                issuing_state: lic.issuing_state.trim() || undefined,
                expiry_date: lic.expiry_date || undefined,
                is_primary: licenses.indexOf(lic) === 0,
              });
            }
          }
        }
        toast.success("Vendor created");
        onCreated?.(result);
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to save vendor");
    }
  };

  const isPending = createVendor.isPending || updateVendor.isPending;
  const stepLabel = isPersonnel ? "Personnel Vendor" : isSub ? "Subcontractor" : "Supplier";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Vendor" : "New Vendor"} — Step {step} of 2</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Company info & contact details"
              : `${stepLabel} — ${isPersonnel ? "Tax info & IC agreement" : isSub ? "Compliance onboarding" : "Supplier details"}`}
          </DialogDescription>
          {!isEdit && step === 1 && (
            <div className="flex items-center gap-2 pt-1">
              <Badge variant="secondary" className="text-[10px]">
                Track: {track === "personnel" ? "🧑 Personnel (Lean)" : "👷 Subcontractor (Full Compliance)"}
              </Badge>
            </div>
          )}
        </DialogHeader>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="v-name">{isPersonnel ? "Full Name *" : "Company Name *"}</Label>
              <Input id="v-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={isPersonnel ? "First Last" : "Vendor name"} />
            </div>

            {!forceTrack && (
              <div>
                <Label>Vendor Type</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {VENDOR_TYPES.map((t) => (
                    <Badge
                      key={t.value}
                      variant={vendorTypes.includes(t.value) ? "default" : "outline"}
                      className="cursor-pointer select-none"
                      onClick={() => toggleType(t.value)}
                    >
                      {t.icon} {t.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="v-contact">{isPersonnel ? "Preferred Name" : "Primary Contact"}</Label>
              <Input id="v-contact" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Contact name" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="v-email">Email</Label>
                <Input id="v-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="v-phone">Phone</Label>
                <Input id="v-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Address</Label>
              <Input value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="Street" />
              <div className="grid grid-cols-3 gap-2">
                <Input value={billingCity} onChange={(e) => setBillingCity(e.target.value)} placeholder="City" />
                <Input value={billingState} onChange={(e) => setBillingState(e.target.value)} placeholder="State" />
                <Input value={billingZip} onChange={(e) => setBillingZip(e.target.value)} placeholder="Zip" />
              </div>
            </div>

            {isPersonnel && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Emergency Contact</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={emergencyContactName} onChange={(e) => setEmergencyContactName(e.target.value)} placeholder="Contact name" />
                  <Input value={emergencyContactPhone} onChange={(e) => setEmergencyContactPhone(e.target.value)} placeholder="Phone" />
                </div>
              </div>
            )}

            {!isPersonnel && (
              <>
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {paymentTerms === "custom" && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Net</span>
                      <Input type="number" min={1} max={365} className="w-24" value={customDays} onChange={(e) => setCustomDays(e.target.value)} placeholder="days" />
                      <span className="text-sm text-muted-foreground">days</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="v-notes">Notes</Label>
                  <Textarea id="v-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP 2 — SUBCONTRACTOR COMPLIANCE ── */}
        {step === 2 && isSub && (
          <div className="space-y-5">
            {/* Compliance Status Banner */}
            <Card className={allComplianceOk ? "border-primary/50 bg-primary/5" : "border-yellow-500/50 bg-accent/50"}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {allComplianceOk
                    ? <CheckCircle2 className="h-5 w-5 text-primary" />
                    : <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  }
                  <span className="font-semibold text-sm">
                    Compliance Status: {allComplianceOk ? "✅ Ready for Approval" : "⚠️ Incomplete"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {complianceChecks().map((c) => (
                    <div key={c.label} className="flex items-center gap-1.5 text-xs">
                      {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : <XCircle className="h-3.5 w-3.5 text-destructive" />}
                      <span className={c.ok ? "text-muted-foreground" : "font-medium"}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* SECTION A — Licenses */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">License & Certifications</span>
              </div>
              {licenses.map((lic, idx) => (
                <div key={idx} className="mb-3 p-3 border rounded-md space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium">License {idx + 1}</span>
                    {licenses.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setLicenses((p) => p.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">License Type</Label>
                      <Select value={lic.license_type} onValueChange={(v) => updateLicense(idx, "license_type", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {LICENSE_TYPES.map((lt) => <SelectItem key={lt} value={lt}>{lt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">License #</Label>
                      <Input className="h-8 text-xs" value={lic.license_number} onChange={(e) => updateLicense(idx, "license_number", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">State</Label>
                      <Input className="h-8 text-xs" maxLength={2} placeholder="TX" value={lic.issuing_state} onChange={(e) => updateLicense(idx, "issuing_state", e.target.value.toUpperCase())} />
                    </div>
                    <div>
                      <Label className="text-xs">Expiry Date</Label>
                      <Input className="h-8 text-xs" type="date" value={lic.expiry_date} onChange={(e) => updateLicense(idx, "expiry_date", e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setLicenses((p) => [...p, emptyLicense()])}>
                <Plus className="h-3 w-3 mr-1" />Add Another License
              </Button>
            </div>

            <Separator />

            {/* SECTION B — Insurance */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Shield className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Insurance Certificates</span>
              </div>
              {insurances.map((ins, idx) => {
                const isRequired = ins.insurance_type === "general_liability" || ins.insurance_type === "workers_comp";
                const isWC = ins.insurance_type === "workers_comp";
                const isExpired = ins.expiry_date && new Date(ins.expiry_date) < new Date();
                const showFields = !isWC || ins.wc_option === "has_coverage";
                return (
                  <Card key={idx} className={`mb-3 ${isExpired ? "border-destructive/50" : ""}`}>
                    <CardHeader className="p-3 pb-1">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-xs font-semibold">{INSURANCE_TYPE_LABELS[ins.insurance_type]}</CardTitle>
                        <div className="flex items-center gap-2">
                          {isRequired && <Badge variant="secondary" className="text-[10px]">⚠️ Required</Badge>}
                          {isExpired && <Badge variant="destructive" className="text-[10px]">Expired</Badge>}
                          {!isRequired && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setInsurances((p) => p.filter((_, i) => i !== idx))}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 pt-1 space-y-2">
                      {/* WC-specific options */}
                      {isWC && (
                        <div className="space-y-1">
                          {WC_OPTIONS.map((opt) => (
                            <label key={opt.value} className="flex items-center gap-2 text-xs cursor-pointer">
                              <input
                                type="radio"
                                name={`wc-option-${idx}`}
                                checked={ins.wc_option === opt.value}
                                onChange={() => updateInsurance(idx, "wc_option", opt.value)}
                                className="accent-primary"
                              />
                              {opt.label}
                            </label>
                          ))}
                          {ins.wc_option !== "has_coverage" && (
                            <p className="text-[10px] text-muted-foreground ml-5">Upload exemption documentation on the vendor detail page after creation.</p>
                          )}
                        </div>
                      )}
                      {showFields && (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Carrier</Label>
                              <Input className="h-8 text-xs" value={ins.carrier_name} onChange={(e) => updateInsurance(idx, "carrier_name", e.target.value)} />
                            </div>
                            <div>
                              <Label className="text-xs">Policy #</Label>
                              <Input className="h-8 text-xs" value={ins.policy_number} onChange={(e) => updateInsurance(idx, "policy_number", e.target.value)} />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Agent Name</Label>
                              <Input className="h-8 text-xs" value={ins.agent_name} onChange={(e) => updateInsurance(idx, "agent_name", e.target.value)} />
                            </div>
                            <div>
                              <Label className="text-xs">Agent Phone</Label>
                              <Input className="h-8 text-xs" value={ins.agent_phone} onChange={(e) => updateInsurance(idx, "agent_phone", e.target.value)} />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Per Occurrence Limit</Label>
                              <Input className="h-8 text-xs" type="number" placeholder="$" value={ins.per_occurrence_limit} onChange={(e) => updateInsurance(idx, "per_occurrence_limit", e.target.value)} />
                            </div>
                            <div>
                              <Label className="text-xs">Aggregate Limit</Label>
                              <Input className="h-8 text-xs" type="number" placeholder="$" value={ins.aggregate_limit} onChange={(e) => updateInsurance(idx, "aggregate_limit", e.target.value)} />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Effective Date</Label>
                              <Input className="h-8 text-xs" type="date" value={ins.effective_date} onChange={(e) => updateInsurance(idx, "effective_date", e.target.value)} />
                            </div>
                            <div>
                              <Label className="text-xs">Expiry Date</Label>
                              <Input className={`h-8 text-xs ${isExpired ? "border-destructive text-destructive" : ""}`} type="date" value={ins.expiry_date} onChange={(e) => updateInsurance(idx, "expiry_date", e.target.value)} />
                            </div>
                          </div>
                          {!isWC && (
                            <div className="flex items-center gap-2 pt-1">
                              <Checkbox checked={ins.company_additional_insured} onCheckedChange={(v) => updateInsurance(idx, "company_additional_insured", !!v)} />
                              <Label className="text-xs">Company named as Additional Insured</Label>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                const existing = insurances.map((i) => i.insurance_type);
                const available: InsuranceType[] = (["umbrella", "commercial_auto", "professional_liability", "builders_risk", "other"] as InsuranceType[])
                  .filter((t) => !existing.includes(t));
                setInsurances((p) => [...p, emptyInsurance(available[0] || "other")]);
              }}>
                <Plus className="h-3 w-3 mr-1" />Add Insurance Policy
              </Button>
            </div>

            <Separator />

            {/* SECTION C — Tax & W9 */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tax & W9</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Tax ID Type</Label>
                  <div className="flex gap-4 mt-1">
                    {[{ v: "ein", l: "EIN" }, { v: "ssn", l: "SSN" }, { v: "itin", l: "ITIN" }].map(({ v, l }) => (
                      <label key={v} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="tax-id-type" checked={taxIdType === v} onChange={() => setTaxIdType(v)} className="accent-primary" />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{taxIdType === "ein" ? "EIN" : taxIdType === "ssn" ? "SSN" : "ITIN"}</Label>
                    <Input className="h-8 text-xs" value={ein || taxId} onChange={(e) => { setEin(e.target.value); setTaxId(e.target.value); }}
                      placeholder={taxIdType === "ein" ? "XX-XXXXXXX" : "XXX-XX-XXXX"} />
                  </div>
                  <div>
                    <Label className="text-xs">Business Type</Label>
                    <Select value={businessType} onValueChange={setBusinessType}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BUSINESS_TYPES.map((bt) => <SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">W9 Received Date</Label>
                    <Input className="h-8 text-xs" type="date" value={w9ReceivedDate} onChange={(e) => setW9ReceivedDate(e.target.value)} />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox id="w9c" checked={w9OnFile} onCheckedChange={(v) => setW9OnFile(!!v)} />
                    <Label htmlFor="w9c" className="text-xs">W9 on file</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="v-1099c" checked={is1099Eligible} onCheckedChange={(v) => setIs1099Eligible(!!v)} />
                    <Label htmlFor="v-1099c" className="text-xs">1099 Eligible</Label>
                  </div>
                </div>
                {is1099Eligible && (
                  <p className="text-[10px] text-muted-foreground">ⓘ Auto-checked for LLCs and Sole Proprietors</p>
                )}
              </div>
            </div>

            <Separator />

            {/* SECTION D — Contract Terms */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ScrollText className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contract & Payment Terms</span>
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Default Retainage %</Label>
                    <Input className="h-8 text-xs" type="number" value={defaultRetainagePct} onChange={(e) => setDefaultRetainagePct(e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Lien Waiver Type</Label>
                    <Select value={lienWaiverType} onValueChange={setLienWaiverType}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LIEN_WAIVER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="v-lienc" checked={lienWaiverRequired} onCheckedChange={(v) => setLienWaiverRequired(!!v)} />
                  <Label htmlFor="v-lienc" className="text-xs">Lien Waiver Required</Label>
                </div>
                <div>
                  <Label className="text-xs">Onboarding Notes</Label>
                  <Textarea className="text-xs" rows={2} value={onboardingNotes} onChange={(e) => setOnboardingNotes(e.target.value)} placeholder="Internal notes for compliance record" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2 — PERSONNEL (LEAN) ── */}
        {step === 2 && isPersonnel && (
          <div className="space-y-5">
            {/* Compliance Status */}
            <Card className={allComplianceOk ? "border-primary/50 bg-primary/5" : "border-yellow-500/50 bg-accent/50"}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {allComplianceOk
                    ? <CheckCircle2 className="h-5 w-5 text-primary" />
                    : <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  }
                  <span className="font-semibold text-sm">
                    {allComplianceOk ? "✅ Ready — Auto-approved on creation" : "⚠️ Incomplete"}
                  </span>
                </div>
                <div className="space-y-1">
                  {personnelChecks().map((c) => (
                    <div key={c.label} className="flex items-center gap-1.5 text-xs">
                      {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : <XCircle className="h-3.5 w-3.5 text-destructive" />}
                      <span className={c.ok ? "text-muted-foreground" : "font-medium"}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Tax Info */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tax Information</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Tax ID Type</Label>
                  <div className="flex gap-4 mt-1">
                    {[{ v: "ssn", l: "SSN" }, { v: "itin", l: "ITIN" }, { v: "ein", l: "EIN (if business)" }].map(({ v, l }) => (
                      <label key={v} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="radio" name="p-tax-type" checked={taxIdType === v} onChange={() => setTaxIdType(v)} className="accent-primary" />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{taxIdType === "ssn" ? "SSN" : taxIdType === "itin" ? "ITIN" : "EIN"}</Label>
                    <Input className="h-8 text-xs" value={taxId} onChange={(e) => { setTaxId(e.target.value); setEin(e.target.value); }}
                      placeholder={taxIdType === "ssn" ? "XXX-XX-XXXX" : taxIdType === "itin" ? "9XX-XX-XXXX" : "XX-XXXXXXX"} />
                    <p className="text-[10px] text-muted-foreground mt-1">⚠️ Encrypted and locked on submission</p>
                  </div>
                  <div>
                    <Label className="text-xs">Business Type</Label>
                    <Select value={businessType} onValueChange={setBusinessType}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual / Sole Proprietor</SelectItem>
                        <SelectItem value="llc">Single Member LLC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="p-w9" checked={w9OnFile} onCheckedChange={(v) => setW9OnFile(!!v)} />
                  <Label htmlFor="p-w9" className="text-xs">W9 on file</Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* IC Agreement */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Scale className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Independent Contractor Agreement</span>
              </div>
              <Card className="bg-muted/30">
                <CardContent className="p-4 text-xs space-y-2">
                  <p className="font-medium">By acknowledging below, the vendor agrees that:</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>They are an independent contractor, not an employee of the contracting company.</li>
                    <li>They are solely responsible for their own federal, state, and local income taxes and self-employment taxes.</li>
                    <li>They will receive IRS Form 1099-NEC if payments exceed $600 in a calendar year.</li>
                    <li>They are not entitled to employee benefits including health insurance, workers compensation, retirement benefits, or paid time off.</li>
                    <li>They have the right to perform services for other clients during the term of any Work Order.</li>
                    <li>All work performed will meet the specifications and standards defined in each Work Order issued by the contracting company.</li>
                  </ol>
                </CardContent>
              </Card>
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2">
                  <Checkbox id="ic-agree" checked={icAgreed} onCheckedChange={(v) => setIcAgreed(!!v)} />
                  <Label htmlFor="ic-agree" className="text-xs font-medium">I have read and agree to the above terms</Label>
                </div>
                <div>
                  <Label className="text-xs">Full Legal Name (type to sign)</Label>
                  <Input className="h-8 text-xs" value={icSignatureName} onChange={(e) => setIcSignatureName(e.target.value)} placeholder="Full legal name" />
                  <p className="text-[10px] text-muted-foreground mt-1">This serves as the vendor's digital signature</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2 — NON-SUB / NON-PERSONNEL ── */}
        {step === 2 && isSupplier && (
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Supplier Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Account Number</Label>
                <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder="Your account with them" />
              </div>
              <div>
                <Label>Credit Limit</Label>
                <Input type="number" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox id="w9s" checked={w9OnFile} onCheckedChange={(v) => setW9OnFile(!!v)} />
                <Label htmlFor="w9s">W9 on file</Label>
              </div>
              <div>
                <Label>EIN / Tax ID</Label>
                <Input value={ein} onChange={(e) => setEin(e.target.value)} placeholder="XX-XXXXXXX" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="v-1099s" checked={is1099Eligible} onCheckedChange={(v) => setIs1099Eligible(!!v)} />
                <Label htmlFor="v-1099s">1099 Eligible</Label>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {step === 1 ? (
            <Button onClick={() => setStep(2)}>Next</Button>
          ) : (
            <Button onClick={handleSave} disabled={isPending}>
              {isPending ? "Saving…" : isEdit ? "Update Vendor" : "Create Vendor"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
